

import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import chronoVideo from '../images/Chrono Session - Gen AI.mp4'
import SessionShareVideo from '../images/Chrono Session - Share Session.mp4'
import AnalyticsVideo from '../images/Chrono Session - Analytics.mp4'

const WhyChooseUsSection = () => {
  const [activeVideoIndex, setActiveVideoIndex] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)

  const videoTitles = [
    { title: "AI Generated", subtitle: "Remembrance", color: "text-blue-600" },
    { title: "Share with", subtitle: "Your Team", color: "text-green-600" },
    { title: "Track Your", subtitle: "Productivity", color: "text-blue-600" }
  ]

  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    const handleScroll = () => {
      const scrollLeft = carousel.scrollLeft
      const cardWidth = 288 + 16 // w-72 (288px) + space-x-4 (16px)
      const newIndex = Math.round(scrollLeft / cardWidth)
      setActiveVideoIndex(Math.max(0, Math.min(newIndex, videoTitles.length - 1)))
    }

    carousel.addEventListener('scroll', handleScroll)
    return () => carousel.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="bg-white">
      {/* Mobile: Unified Video Carousel - Only show on mobile */}
      <div className="lg:hidden py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Dynamic Title */}
          <motion.div
            key={activeVideoIndex}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center mb-6"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              {videoTitles[activeVideoIndex].title}{' '}
              <span className={videoTitles[activeVideoIndex].color}>
                {videoTitles[activeVideoIndex].subtitle}
              </span>
            </h2>
          </motion.div>
          
          <div className="w-full overflow-hidden">
            <div 
              ref={carouselRef}
              className="flex overflow-x-auto space-x-4 pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth touch-pan-x px-4" 
              style={{ scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
            >
              {/* Card 1 */}
              <div className="flex-shrink-0 w-72 rounded-2xl shadow-xl bg-gradient-to-br from-blue-50 to-blue-100 p-0 border border-blue-100 relative snap-center" style={{ boxShadow: '0 4px 32px 0 rgba(60, 80, 180, 0.06) inset' }}>
                <div className="w-full rounded-t-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-4 pt-4 pb-2">
                  <span className="text-lg font-bold text-white block" style={{ fontFamily: 'Google Sans, Inter, system-ui, sans-serif' }}>
                    AI-Powered Organization
                  </span>
                </div>
                <div className="w-full flex flex-col items-center justify-center py-4">
                  <div className="w-[92%] rounded-lg border-4 border-blue-100 shadow-lg overflow-hidden bg-blue-50">
                    <video
                      src={chronoVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
              {/* Card 2 */}
              <div className="flex-shrink-0 w-72 rounded-2xl shadow-xl bg-gradient-to-br from-green-50 to-green-100 p-0 border border-green-100 relative snap-center" style={{ boxShadow: '0 4px 32px 0 rgba(60, 80, 180, 0.06) inset' }}>
                <div className="w-full rounded-t-2xl bg-gradient-to-r from-green-600 to-green-700 px-4 pt-4 pb-2">
                  <span className="text-lg font-bold text-white block" style={{ fontFamily: 'Google Sans, Inter, system-ui, sans-serif' }}>
                    Share Sessions Instantly
                  </span>
                </div>
                <div className="w-full flex flex-col items-center justify-center py-4">
                  <div className="w-[92%] rounded-lg border-4 border-green-100 shadow-lg overflow-hidden bg-gray-50">
                    <video
                      src={SessionShareVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
              {/* Card 3 */}
              <div className="flex-shrink-0 w-72 rounded-2xl shadow-xl bg-gradient-to-br from-blue-50 to-indigo-100 p-0 border border-blue-100 relative snap-center" style={{ boxShadow: '0 4px 32px 0 rgba(60, 80, 180, 0.06) inset' }}>
                <div className="w-full rounded-t-2xl bg-gradient-to-r from-blue-700 to-indigo-600 px-4 pt-4 pb-2">
                  <span className="text-lg font-bold text-white block" style={{ fontFamily: 'Google Sans, Inter, system-ui, sans-serif' }}>
                    Team Collaboration
                  </span>
                </div>
                <div className="w-full flex flex-col items-center justify-center py-4">
                  <div className="w-[92%] rounded-lg border-4 border-blue-100 shadow-lg overflow-hidden bg-blue-50">
                    <video
                      src={AnalyticsVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Redesigned - Left Text, Right Gradient Card with Video */}
      <section className="hidden lg:block py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
            >
                  <div className="space-y-6">
                <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight">
                  AI Generated <span className='text-blue-600'>Remembrance</span>
                </h2>
                {/* Hide paragraph on mobile, show on desktop */}
                <p className="hidden md:block text-md md:text-xl text-gray-600 text-justify leading-relaxed">
                Transform your browsing tension into organized brilliance with a single click. Our AI-powered session manager instantly captures, categorizes, and organizes your tabs into meaningful sessions that boost your productivity.
                </p>
                <ul className="space-y-3 mt-4 ">
                  {/* Show only first point on mobile, all on desktop */}
                  <li className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                    <span className="text-gray-700 text-base font-medium">
                      Instantly groups your open tabs into smart sessions.
                    </span>
                  </li>
                  <li className="hidden md:flex items-start">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                    <span className="text-gray-700 text-base font-medium">
                      Remembers your research and work context for easy return.
                    </span>
                  </li>
                  <li className="hidden md:flex items-start">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                    <span className="text-gray-700 text-base font-medium">
                      No more lost tabs—find everything, anytime, on any device.
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Right Content - Desktop Only */}
            <motion.div
              className="hidden lg:flex flex-col items-center mt-2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
            >
              <div className="w-full max-w-xl rounded-2xl shadow-xl bg-gradient-to-br from-blue-50 to-blue-100 p-0 border border-blue-100 relative" style={{ boxShadow: '0 4px 32px 0 rgba(60, 80, 180, 0.06) inset' }}>
                {/* Title bar with darker gradient */}
                <div className="w-full rounded-t-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-10 pt-5 pb-3">
                  <span className="text-2xl md:text-3xl font-bold text-white block" style={{ fontFamily: 'Google Sans, Inter, system-ui, sans-serif' }}>
                    AI-Powered Organization
                  </span>
                </div>
                <div className="w-full flex flex-col items-center justify-center py-8">
                  <div className="w-[92%] rounded-lg border-8 border-blue-100 shadow-lg overflow-hidden bg-blue-50">
                    <video
                      src={chronoVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-[320px] md:h-[400px] object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3: Left Gradient Card with Video, Right Text */}
      <section className="hidden lg:block py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Desktop Only */}
            <motion.div
              className="hidden lg:flex flex-col items-center mt-2"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
            >
              <div className="w-full max-w-xl rounded-2xl shadow-xl bg-gradient-to-br from-green-50 to-green-100 p-0 border border-green-100 relative" style={{ boxShadow: '0 4px 32px 0 rgba(60, 80, 180, 0.06) inset' }}>
                {/* Title bar with darker gradient */}
                <div className="w-full rounded-t-2xl bg-gradient-to-r from-green-600 to-green-700 px-10 pt-5 pb-3">
                  <span className="text-2xl md:text-3xl font-bold text-white block" style={{ fontFamily: 'Google Sans, Inter, system-ui, sans-serif' }}>
                    Share Sessions Instantly
                  </span>
                </div>
                <div className="w-full flex flex-col items-center justify-center py-8">
                  <div className="w-[92%] rounded-lg border-8 border-green-100 shadow-lg overflow-hidden bg-gray-50">
                    <video
                      src={SessionShareVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-[320px] md:h-[400px] object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
           

            {/* Right */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
            >
              <div className="space-y-6">
                <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Share with <span className='text-green-600'>Your Team</span>
                </h2>
                {/* Hide paragraph on mobile, show on desktop */}
                <p className="hidden md:block text-md  md:text-xl text-gray-600 text-justify leading-relaxed">
                Share your organized sessions with teammates instantly. Perfect for research sharing, project handoffs, and collaborative workflows. One link opens the entire session.
                </p>
                <ul className="space-y-3 mt-2">
                  {/* Show only first point on mobile, all on desktop */}
                  <li className="flex items-start text-gray-700 text-base md:text-lg font-medium">
                    <span className="inline-block w-2 h-2 mt-2 mr-3 rounded-full bg-green-500" />
                    Instantly share sessions with a single link
                  </li>
                  <li className="hidden md:flex items-start text-gray-700 text-base md:text-lg font-medium">
                    <span className="inline-block w-2 h-2 mt-2 mr-3 rounded-full bg-green-500" />
                    Collaborate with your team in real time
                  </li>
                  <li className="hidden md:flex items-start text-gray-700 text-base md:text-lg font-medium">
                    <span className="inline-block w-2 h-2 mt-2 mr-3 rounded-full bg-green-500" />
                    No more sending endless tabs or screenshots
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 4: Left Text, Right Gradient Card with Video */}
      <section className="hidden lg:block py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
            >
              <div className="space-y-6">
                <h2 className="text-3xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Track Your <span className='text-blue-600'>Productivity</span>
                </h2>
                {/* Hide paragraph on mobile, show on desktop */}
                <p className="hidden md:block text-md md:text-xl text-gray-600 text-justify leading-relaxed">
                  Get insights into your browsing patterns and productivity. See which sessions you use most, track your focus time, and optimize your workflow with detailed analytics.
                </p>
                <ul className="space-y-3 mt-2">
                  {/* Show only first point on mobile, all on desktop */}
                  <li className="flex items-start text-gray-700 text-base md:text-lg font-medium">
                    <span className="inline-block w-2 h-2 mt-2 mr-3 rounded-full bg-blue-500" />
                    Instantly see your most productive sessions
                  </li>
                  <li className="hidden md:flex items-start text-gray-700 text-base md:text-lg font-medium">
                    <span className="inline-block w-2 h-2 mt-2 mr-3 rounded-full bg-blue-500" />
                    Track your focus time and browsing trends
                  </li>
                  <li className="hidden md:flex items-start text-gray-700 text-base md:text-lg font-medium">
                    <span className="inline-block w-2 h-2 mt-2 mr-3 rounded-full bg-blue-500" />
                    Optimize your workflow with actionable insights
                  </li>
                </ul>
              </div>
            </motion.div>
            
            {/* Right - Desktop Only */}
            <motion.div
              className="hidden lg:flex flex-col items-center mt-2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
            >
              <div className="w-full max-w-xl rounded-2xl shadow-xl bg-gradient-to-br from-blue-50 to-indigo-100 p-0 border border-blue-100 relative" style={{ boxShadow: '0 4px 32px 0 rgba(60, 80, 180, 0.06) inset' }}>
                {/* Title bar with darker gradient */}
                <div className="w-full rounded-t-2xl bg-gradient-to-r from-blue-700 to-indigo-600 px-10 pt-5 pb-3">
                  <span className="text-2xl md:text-3xl font-bold text-white block" style={{ fontFamily: 'Google Sans, Inter, system-ui, sans-serif' }}>
                    Team Collaboration
                  </span>
                </div>
                <div className="w-full flex flex-col items-center justify-center py-8">
                  <div className="w-[92%] rounded-lg border-8 border-blue-100 shadow-lg overflow-hidden bg-blue-50">
                    <video
                      src={AnalyticsVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-[320px] md:h-[400px] object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default WhyChooseUsSection 